/// <amd-module name="Tfs/KeyboardShortcutsUI/ShortcutsPanel" />
import * as React from "react";

import * as Resources from "Tfs/KeyboardShortcutsUI/Resources";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { Components, useComponentContext } from "VSS/Platform/Layout";
import { TitleSize } from "VSSUI/Header";
import { Link } from "VSSUI/Link";
import { Panel } from "VSSUI/Panel";
import { Toggle } from "VSSUI/Toggle";

interface IKeyboardShortcutsPanelProps {
    onDismiss: () => void;
}

function isMacOrIOS(): boolean {
    return navigator.userAgent.indexOf("Mac OS X") > -1 || !!/iphone|ipod|ipad/i.exec(navigator.userAgent);
}

const KeyboardShortcutsPanel = (props: IKeyboardShortcutsPanelProps): JSX.Element => {
    const componentContext = useComponentContext();
    const shortcutsService = componentContext.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
    const groups = shortcutsService.getShortcutGroups();
    const usesCommandKey = isMacOrIOS();

    return (
        <Panel onDismiss={props.onDismiss} titleProps={{ size: TitleSize.Large, text: Resources.KeyboardShortcuts }}>
            <div className="flex-column flex-grow rhythm-vertical-32">
                <div className="flex-row margin-top-8 enable-shortcuts">
                    <div>{Resources.EnableShortcuts}</div>
                    <Toggle
                        offText={Resources.Disabled}
                        onText={Resources.Enabled}
                        checked={shortcutsService.getShortcutsEnabled()}
                        onChange={(event, value) => shortcutsService.setShortcutsEnabled(value)}
                    />
                </div>
                <Link href="https://go.microsoft.com/fwlink/?LinkID=708466" target="_blank">
                    {Resources.KeyboardShortcutsFullList}
                </Link>
                {groups.map(group => {
                    const shortcuts = group.shortcuts.filter(s => !s.hideFromHelpDialog && s.combosToDisplay && s.combosToDisplay.length > 0);
                    if (shortcuts.length === 0) {
                        return null;
                    }

                    return (
                        <div className="flex-column" key={group.name}>
                            <div className="title-s">{group.name}</div>
                            <ul className="keyboard-shortcuts-grouping flex-column">
                                {shortcuts.map(shortcut => (
                                    <li className="keyboard-shortcut-entry flex-row flex-center" key={shortcut.combosToDisplay!.join(";")}>
                                        <span className="flex-grow">{shortcut.description}</span>
                                        <span className="keyboard-shortcut-combos flex-row flex-center rhythm-horizontal-8">
                                            {shortcut.combosToDisplay!.map((combo, index) => {
                                                const keys: JSX.Element[] = [];

                                                if (index > 0) {
                                                    keys.push(
                                                        <span className="margin-horizontal-4" key={index}>
                                                            /
                                                        </span>
                                                    );
                                                }

                                                // If mod is given then it means its a wildcard for Command or Control based on your OS
                                                // Change the help based on what key it will be.
                                                keys.push(
                                                    ...combo
                                                        .replace(/\bmod\b/gi, usesCommandKey ? "⌘" : "ctrl")
                                                        .split(" ")
                                                        .map((k, i) => (
                                                            <kbd className="body-l" key={i}>
                                                                {k}
                                                            </kbd>
                                                        ))
                                                );

                                                return keys;
                                            })}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })}
            </div>
        </Panel>
    );
};

Components.add("KeyboardShortcutsPanel", KeyboardShortcutsPanel);
